import { Temporal } from 'temporal-polyfill'
import { includes, padStart } from 'lodash-es'
import { YEAR_ONLY_CUTOFF_YEAR } from '@constants/date.js'
import {
  toUTCDateTimeString as toUTCDateTimeStringUtil,
  isTemporalPlainDate,
  isTemporalInstant,
  isTemporalZonedDateTime,
  isTemporalPlainDateOrZonedDateTime,
} from '@utils/temporal.js'

// A wrapper class providing an interface to something temporal (i.e. Instant, PlainDateTime, ZonedDateTime)
export default class TimetoastTemporal {
  // calculated min/max EpochMilliseconds
  static MS_MIN = -8640000000000000
  static MS_MAX = 8640000000000000

  // Note: We should let this class throw JS Temporal errors, instead of defensively programming around RangeErrors etc.

  constructor(obj) {
    this.temporal = null
    this.yearOnly = false // so we can potentially use the temporal instance for arithmitic, but just display the year in UI
    this.isBC = false
    if (obj) {
      const objType = typeof obj
      switch (objType) {
        case 'number': {
          // Assume in EpochMilliseconds, like the legacy Date.getTime() returned

          /* // To allow easy debugging 
          const lessThanSupportedRange = obj < TimetoastTemporal.MS_MIN
          const greaterThanSupportedRange = obj > TimetoastTemporal.MS_MAX
          if (lessThanSupportedRange || greaterThanSupportedRange){
            console.warn(obj, "is outside the supported EpochMilliseconds range.")
          }
          */
          const instant = Temporal.Instant.fromEpochMilliseconds(obj)
          this.temporal = instant.toZonedDateTimeISO('UTC')
          break
        }
        case 'object':
          if (isTemporalPlainDateOrZonedDateTime(obj)) {
            this.temporal = obj
          } else if (isTemporalInstant(obj)) {
            this.temporal = obj.toZonedDateTime('UTC')
          } else if (isTemporalPlainDate(obj)) {
            this.temporal = obj.toZonedDateTime('UTC')
          } else {
            console.warn(`Unexpected argument with classname ${objType}sed to TimetoastTemporal constructor`)
          }
          break
        default:
          console.warn(`Unexpected argument type ${objType} passed to constructor`)
      }
      this.updateIsBC()
    }
  }

  static parse(input) {
    //console.log("Gonna parse", input)
    if (input === null || input === undefined) {
      throw new Error(`Can't parse ${input} into a TimetoastTemporal`)
    }
    let zonedDateTime
    let timetoastTemporal = new TimetoastTemporal()
    let splitted, year, yearAbs

    // get the type of the input. If it's an object, use the constructor class name instead
    let typeOfInput = typeof input
    if (typeOfInput === 'object') {
      // Note: We used to just set typeOfInput to input.constructor.name, but that gets mangled during code minification step
      if (isTemporalZonedDateTime(input)) {
        typeOfInput = 'Temporal.ZonedDateTime'
      } else {
        // we shouldn't really be getting here....
        if (isTemporalInstant(input)) {
          typeOfInput = 'Temporal.Instant'
        } else if (isTemporalPlainDate(input)) {
          typeOfInput = 'Temporal.PlainDate'
        }
      }
    }
    switch (typeOfInput) {
      case 'string': {
        const startsWithMinus = input.charAt(0) === '-'
        const stringToSplit = startsWithMinus ? input.substring(1) : input
        splitted = stringToSplit.split(/[- :]/)
        const isIsoDateStringFormat = includes(input, 'T') // Super simple, but it works for our purposes
        if (isIsoDateStringFormat) {
          // looks like it's probably an ISO 8601 formatted string (we currently accept 2021-01-01T00:00:00Z, 2021-01-01T00:00:00+00:00, 2021-01-01T00:00:00-00:00)
          // we can't currently rely on the Temporal.Instant.from function to deal properly with far future and far past years
          // for this reason, we manually construct UTC-based
          yearAbs = parseInt(splitted[0])
          year = startsWithMinus ? -yearAbs : yearAbs
          // first check if the year is supported by the Temporal.Instant.from function

          if (year < 9999) {
            try {
              let instant
              if (startsWithMinus) {
                // may need to do some work to make sure year can be parsed correctly by 0-padding it
                if (splitted[0].length < 6) {
                  // first split on the T char into separate date and time parts
                  const dateAndTimeSplit = stringToSplit.split(/[T]/)
                  // then split into year, hour and day parts
                  const dateSplitted = dateAndTimeSplit[0].split(/-/)
                  // pad the year to at least 6 chars in length
                  dateSplitted[0] = padStart(dateSplitted[0], 6, '0')
                  // munge them back together so we can create the instant
                  const mungedISODateString = `-${dateSplitted.join('-')}T${dateAndTimeSplit[1]}`
                  instant = Temporal.Instant.from(mungedISODateString)
                } else {
                  instant = Temporal.Instant.from(input)
                }
              } else {
                instant = Temporal.Instant.from(input)
              }
              timetoastTemporal.temporal = instant.toZonedDateTimeISO('UTC')
            } catch {
              console.warn(
                'Failed to parse that ISO 8601 string, manually create a zoned date time and turn that into an instant'
              )
              const yearOnly = year < YEAR_ONLY_CUTOFF_YEAR
              zonedDateTime = Temporal.ZonedDateTime.from({
                timeZone: 'UTC',
                year: year,
                month: yearOnly ? 1 : splitted[1],
                day: yearOnly ? 1 : splitted[2],
                hour: yearOnly ? 0 : splitted[3],
                minute: yearOnly ? 0 : splitted[4],
                second: yearOnly ? 0 : splitted[5],
              })
              timetoastTemporal.temporal = zonedDateTime
              timetoastTemporal.yearOnly = yearOnly
            }
          } else {
            // we won't be able to parse this far-future year using standard Temporal.Instant.from, we'll need to try and
            // manually construct a ZonedDateTime and convert that to an instant
            const manualSplitted = stringToSplit.split('T')
            const dateString = manualSplitted[0]
            const timeString = manualSplitted[1]
            const dateSplitted = dateString.split('-')
            let timeSplitted
            if (includes(timeString, 'Z')) {
              // zulu time (use UTC)
              timeSplitted = timeString.split('Z')[0].split(':')
              zonedDateTime = Temporal.ZonedDateTime.from({
                timeZone: 'UTC',
                year: year,
                month: dateSplitted[1],
                day: dateSplitted[2],
                hour: timeSplitted[0],
                minute: timeSplitted[1],
                second: timeSplitted[2],
                millisecond: timeSplitted[3],
              })
              timetoastTemporal.temporal = zonedDateTime
            } else if (includes(timeString, '+') || includes('-')) {
              // Includes a time offset, not a timezone
              // We will need to look at implementing this in the future
              throw new Error('TBI...')
            }
          }
        } else {
          // Strings without any timezone data; assume any time data is UTC
          // When we used JavaScript date object, we assumed UTC timezone so we could do arithmitic

          // grab the year from the split array
          yearAbs = parseInt(splitted[0])
          const year = startsWithMinus ? -yearAbs : yearAbs

          // create a UTC zoned datetime, and then convert that to an instant
          // decide if we're going to keep any month/day data.
          // We don't if we're only provided a string with a year, or the year is below cutoff for recorded history
          const yearOnly = splitted.length === 1 || year < YEAR_ONLY_CUTOFF_YEAR
          timetoastTemporal.yearOnly = yearOnly

          // we only ever let people addd year, month and day
          const plainDate = Temporal.PlainDate.from({
            year: year,
            month: yearOnly ? 1 : splitted[1],
            day: yearOnly ? 1 : splitted[2],
          })
          zonedDateTime = plainDate.toZonedDateTime('UTC')
          timetoastTemporal.temporal = zonedDateTime
        }
        break
      }
      case 'number': {
        // probably epoch milliseconds, represents an exact time....
        const instant = Temporal.Instant.fromEpochMilliseconds(input)
        timetoastTemporal.temporal = instant.toZonedDateTimeISO('UTC')
        break
      }

      case 'Temporal.ZonedDateTime':
        timetoastTemporal.temporal = input
        break

      default:
        throw new Error(
          `You were trying to pass -->${input}<-- to TimetoastTemporal.parse which was of type -->${typeOfInput}<--. This is not (yet) supported.`
        )
    }

    timetoastTemporal.updateIsBC()
    //console.log(`Parsed ${input} into ${timetoastTemporal.temporal}`)
    return timetoastTemporal
  }

  /**
   * @param {string} year - A year.
   * @param {string} month - An integer between 1 and 12.
   * @param {string} day - An integer between 1 and 31.
   * @param {string} hour - An integer between 0 and 23.
   * @param {string} minute - An integer between 0 and 59.
   * @param {string} second - An integer between 0 and 59
   * @param {string} millisecond - An integer between 0 and 999
   */
  static fromUTC(year = 1901, month = 1, day = 1, hour = 0, minute = 0, second = 0, millisecond = 0) {
    // Note: Although peculiar, you may need to initialize with an incorrect year, before updating it
    let updateYear = year < 100 || year > 9999
    let initYear = year
    if (updateYear) {
      initYear = 1978
    }
    let initObject = {
      timeZone: 'UTC',
      year: initYear,
      month,
      day,
      hour,
      minute,
      second,
      millisecond,
    }
    let zonedDateTime = Temporal.ZonedDateTime.from(initObject)
    if (updateYear) {
      zonedDateTime = zonedDateTime.with({ year })
    }
    return new TimetoastTemporal(zonedDateTime)
  }

  getTime() {
    let result = null
    if (this.temporal) {
      if (isTemporalPlainDate(this.temporal)) {
        const zonedDateTime = this.temporal.toZonedDateTime('UTC')
        result = zonedDateTime.epochMilliseconds
      } else if (isTemporalZonedDateTime(this.temporal) || isTemporalInstant(this.temporal)) {
        result = this.temporal.epochMilliseconds
      } else {
        console.warn('Unsupported object')
      }
    }
    return result
  }

  toString() {
    return this.temporal.toString()
  }

  toUTCDateTimeString() {
    return toUTCDateTimeStringUtil(this.temporal)
  }

  // updates derived variables (e.g. isBC)
  // Note: Don't automatically set yearOnly, as this should be determined at parse time.
  updateIsBC() {
    if (this.temporal) {
      try {
        if (isTemporalPlainDate(this.temporal)) {
          this.isBC = this.temporal.year <= 0
        } else if (isTemporalInstant(this.temporal)) {
          let utcZonedDateTime = this.temporal.toZonedDateTimeISO('UTC')
          this.isBC = utcZonedDateTime.withCalendar('gregory').era === 'bce'
        } else if (isTemporalZonedDateTime(this.temporal)) {
          this.isBC = this.temporal.withCalendar('gregory').era === 'bce'
        } else {
          throw `Don't know how to deal with temporal instance ${this.temporal}`
        }
      } catch (e) {
        // There seems to be a bug in @js-temporal/polyfill (version 0.3.0) specific to Firefox 96.0 (Note: Newer version of the Firefox don't seem to experience the problem)
        // A RangeError is thrown when running the following code in that particular browser version:
        //  Temporal.ZonedDateTime.from({ timeZone: 'UTC', year: 2020, month: 1, day: 1 }).withCalendar('gregory').era
        //  Note: The error message is: "Era a (ISO year 2020) was not matched by any era"
        //
        // As a temporary application-level fix for this issue, we use a simplified BCE check here. A future version
        // of @js-temporal/polyfill may fix this issue, but it should be checked against Firefox 96.0 before removing this error handling code branch
        this.isBC = this.temporal.year <= 0
      }
    }
  }
}
// checks type using instanceof, or falls back on duck-typing
export function isTimetoastTemporal(obj) {
  return obj instanceof TimetoastTemporal || (obj.hasOwnProperty('temporal') && obj.hasOwnProperty('yearOnly'))
}
